import React from "react";

import { Card, Col, Form, Row } from "react-bootstrap";

import {
  financingStatusOptions,
  landStatusOptions,
  projectTypeOptions,
  yesNoOptions,
} from "@/context/ContactProvider.context";
import { formatFormOptionValue } from "@/utils/Toolbox";

import i18n from "../../../i18n";

interface ContactFormProjectInfoSectionProps {
  methods: any;
}

const ContactFormProjectInfoSection: React.FC<
  ContactFormProjectInfoSectionProps
> = ({ methods }): React.JSX.Element => {
  const { t } = i18n;
  const projectType = methods.watch("projectInfo.type");

  return (
    <Card className="mt-4">
      <Card.Body>
        <Row>
          <Col xs={12}>
            <Card.Title>{t("Project Information")}</Card.Title>
          </Col>

          <Col xs={12}>
            <Row>
              <Col lg={6} xs={12}>
                <Form.Group className="mb-3" controlId="project-type">
                  <Form.Label>{t("Type of housing")}</Form.Label>
                  <div className="d-flex flex-wrap flex-fill gap-2 align-content-stretch">
                    {projectTypeOptions.map((v, i) => (
                      <Form.Check
                        key={i}
                        className="radio-pill"
                        id={`project-type-${v}`}
                        label={t(v)}
                        type="radio"
                        value={v}
                        {...methods.register("projectInfo.type", {
                          onChange: () => methods.trigger("projectInfo.type"),
                        })}
                        isInvalid={!!methods.formState.errors.projectInfo?.type}
                        name="projectInfo.type"
                      />
                    ))}
                  </div>
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {t(methods.formState.errors.projectInfo?.type?.message)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {projectType === "Other" && (
                <Col lg={6} xs={12}>
                  <Form.Group className="mt-4" controlId="project-type-other">
                    <Form.Control
                      placeholder="Describe 'Other'..."
                      type="text"
                      {...methods.register("projectInfo.typeOther")}
                      isInvalid={
                        !!methods.formState.errors.projectInfo?.typeOther
                      }
                    />
                    <Form.Control.Feedback className="d-block" type="invalid">
                      {t(
                        methods.formState.errors.projectInfo?.typeOther
                          ?.message,
                      )}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Col>

          <Col xs={12}>
            <Form.Group className="mb-3" controlId="project-count">
              <Form.Label>{t("Number of units")}</Form.Label>
              <Form.Control
                type="number"
                {...methods.register("projectInfo.count", {
                  valueAsNumber: true,
                })}
                defaultValue={0}
                isInvalid={!!methods.formState.errors.projectInfo?.count}
                max={999}
                min={0}
              />
              <Form.Control.Feedback type="invalid">
                {t(methods.formState.errors.projectInfo?.count?.message)}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={4} xs={12}>
            <Form.Group className="mb-3" controlId="project-land-status">
              <Form.Label>{t("Land status")}</Form.Label>
              {landStatusOptions.map((v, i) => (
                <Form.Check
                  key={i}
                  className="radio-pill"
                  id={`project-land-status-${v}`}
                  label={t(formatFormOptionValue(v))}
                  type="radio"
                  value={v}
                  {...methods.register("projectInfo.landStatus", {
                    onChange: () => methods.trigger("projectInfo.landStatus"),
                  })}
                  isInvalid={!!methods.formState.errors.projectInfo?.landStatus}
                />
              ))}
              <Form.Control.Feedback className="d-block" type="invalid">
                {t(methods.formState.errors.projectInfo?.landStatus?.message)}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={4} xs={12}>
            <Form.Group className="mb-3" controlId="project-financing-status">
              <Form.Label>{t("Financing status")}</Form.Label>
              {financingStatusOptions.map((v, i) => (
                <Form.Check
                  key={i}
                  className="radio-pill"
                  id={`project-financing-status-${v}`}
                  label={t(formatFormOptionValue(v))}
                  type="radio"
                  value={v}
                  {...methods.register("projectInfo.financingStatus", {
                    onChange: () =>
                      methods.trigger("projectInfo.financingStatus"),
                  })}
                  isInvalid={
                    !!methods.formState.errors.projectInfo?.financingStatus
                  }
                />
              ))}
              <Form.Control.Feedback className="d-block" type="invalid">
                {t(
                  methods.formState.errors.projectInfo?.financingStatus
                    ?.message,
                )}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={4} xs={12}>
            <Form.Group className="mb-3" controlId="project-requires-financing">
              <Form.Label>
                {t("Would you like us to provide financing options?")}
              </Form.Label>
              {yesNoOptions.map((v, i) => (
                <Form.Check
                  key={i}
                  className="radio-pill"
                  id={`project-requires-financing-${v}`}
                  label={t(formatFormOptionValue(v))}
                  type="radio"
                  value={v}
                  {...methods.register("projectInfo.requiresFinancing", {
                    onChange: () =>
                      methods.trigger("projectInfo.requiresFinancing"),
                  })}
                  isInvalid={
                    !!methods.formState.errors.projectInfo?.requiresFinancing
                  }
                />
              ))}
              <Form.Control.Feedback className="d-block" type="invalid">
                {t(
                  methods.formState.errors.projectInfo?.requiresFinancing
                    ?.message,
                )}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group className="mb-3" controlId="projectInfo-additionalInfo">
              <Form.Label>
                {t("Please provide additional information")}
              </Form.Label>
              <Form.Control
                as="textarea"
                type="text"
                {...methods.register("projectInfo.additionalInfo")}
                isInvalid={
                  !!methods.formState.errors.projectInfo?.additionalInfo
                }
              />
              <Form.Control.Feedback type="invalid">
                {t(
                  methods.formState.errors.projectInfo?.additionalInfo?.message,
                )}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ContactFormProjectInfoSection;
